import { useFormik } from 'formik';
import React, {useEffect, useState, useRef} from 'react'
import { Form, Image, Modal } from 'react-bootstrap'
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import Button from 'react-bootstrap-button-loader';

const validationSchema = Yup.object().shape({
   otp: Yup.string().required('Otp is required').min(4, 'otp is invalid')
});

// --- Good practice: Define countdown time as a constant ---
const RESEND_OTP_TIMER = 30; // in seconds

export const OtpModal = ({showOtpModal, setShowOtpModal, loginSubmit, resendOtp, loader, otpContext = 'email'}) => {
    // otpContext can be: 'email', 'mobile', or 'both' (default)
    
    const [timer, setTimer] = useState(RESEND_OTP_TIMER);
    const [canResend, setCanResend] = useState(false);

    // Use useRef to persist the interval ID across renders
    const intervalRef = useRef(null);

    const otpForm = useFormik({
     initialValues: {
        otp: ''
     },
     onSubmit: values => {
        loginSubmit(values.otp);
     },
     validationSchema
    });

    // Create a reusable function to start the timer
    const startTimer = () => {
        // Clear any existing interval before starting a new one
        if (intervalRef.current) clearInterval(intervalRef.current);

        setCanResend(false);
        setTimer(RESEND_OTP_TIMER);

        intervalRef.current = setInterval(() => {
            setTimer(prevTimer => {
                if (prevTimer <= 1) {
                    clearInterval(intervalRef.current);
                    setCanResend(true);
                    return 0;
                }
                return prevTimer - 1;
            });
        }, 1000);
    };

    // This effect now only has one job: start the timer when the modal opens.
    useEffect(() => {
        if (showOtpModal) {
            otpForm.resetForm();
            startTimer(); // Call our reusable function
        }
        
        // The cleanup function is crucial to prevent memory leaks when the modal closes
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOtpModal]);

    const handleResendClick = () => {
        if (canResend) {
            resendOtp();
            // --- CHANGE 4: Restart the timer after resending ---
            startTimer();
        }
    };

    return (
        <Modal
            show={showOtpModal}
            onHide={() => setShowOtpModal(false)}
            backdrop="static"
            keyboard={false}
            size="md"
            centered
        >
            <Modal.Header closeButton style={{ border: '0px'}}/>
            <Modal.Body>
                <div className="text-center px-4 pt-4">
                    <Image src='/images/icon-otp-verification.svg' className='mx-auto' />
                    <h5 className='fw-bold mt-4'>OTP Verification</h5>
                    <p className='mb-5'>
                        One time password sent to your {
                            otpContext === 'email' ? 'registered email address' : 
                            otpContext === 'mobile' ? 'registered mobile number' : 
                            'registered email address/mobile number'
                        }
                    </p>
                    <Form className='mx-5' noValidate onSubmit={otpForm.handleSubmit}>
                        <Form.Group className="mb-3" controlId="text-otp">
                            <Form.Control
                                type="tel"
                                placeholder="Enter OTP"
                                className='text-center'
                                name="otp"
                                maxLength={6}
                                onChange={otpForm.handleChange}
                                value={otpForm.values.otp}
                                onBlur={otpForm.handleBlur}
                                isInvalid={otpForm.touched.otp && !!otpForm.errors.otp}
                            />
                            <Form.Control.Feedback type="invalid">
                                {otpForm.errors.otp}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <div className="d-flex justify-content-between mb-3">
                            <p className="mb-0 small text-black-50">Didn't receive the OTP?</p>
                            {/* --- CHANGE 4: Conditionally render the resend link or timer --- */}
                            {canResend ? (
                                <Link onClick={handleResendClick} className='small text-primary fw-medium'>
                                    Resend OTP
                                </Link>
                            ) : (
                                <p className='small text-muted fw-medium mb-0'>
                                    Resend in {timer}s
                                </p>
                            )}
                        </div>
                    </Form>
                </div>
            </Modal.Body>
            <Modal.Footer className='border-0 px-4 pb-4'>
                <Button
                    variant="dark"
                    className='w-100'
                    onClick={otpForm.handleSubmit}
                    loading={loader}
                >
                    Confirm
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
